import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../partage/api/client';
import { cles } from '../partage/relectures';
import { useAuPouce } from '../partage/surface';
import { motDeCompte, motsDuDossier } from '../partage/vocabulaire';
import { teinteOuAccent } from '../apparence';
import './Deplacer.css';

/**
 * Choisir une destination
 *
 * **La destination se choisit par DESCENTE, jamais dans une liste plate** (SPEC §3) : on
 * clique un dossier, on voit ce qu'il contient, on descend autant qu'on veut, et on dépose
 * **au niveau où l'on est**.
 *
 * Deux raisons, et la seconde compte plus :
 *   · c'est le geste de la carte réemployé — un mécanisme de moins à apprendre ;
 *   · ça évite de larguer une bulle à la racine d'un projet, où elle serait perdue.
 *
 * Aucun glisser (principe 8) : on choisit, puis on désigne où.
 *
 * Le bouton de dépôt se teint de **la couleur du dossier d'arrivée**, jamais du bleu-vert :
 * le bleu-vert dit « toi, l'acquis », la couleur du dossier dit « ici » (AGENTS, règle 3).
 */
export function Deplacer({
  /** La bulle qu'on déplace : ni elle ni son contenu ne peuvent l'accueillir. */
  bulleId,
  titre,
  combienDedans = 0,
  onDeposer,
  onRenoncer,
}: {
  bulleId: string;
  titre: string;
  /** Ce qui part avec elle. Le dire avant le dépôt évite la surprise après. */
  combienDedans?: number;
  /** `dossierId` : le dossier d'arrivée, `null` quand la bulle devient elle-même un dossier. */
  onDeposer: (parentId: string | null, dossierId: string | null) => void;
  onRenoncer: () => void;
}) {
  const [ou, setOu] = useState<string | null>(null);
  const m = motsDuDossier(useAuPouce());

  const dossiers = useQuery({ queryKey: cles.dossiers, queryFn: api.dossiers, enabled: ou === null });
  const feuille = useQuery({
    queryKey: cles.feuille(ou ?? ''),
    queryFn: () => api.feuille(ou as string),
    enabled: ou !== null,
  });

  const dedans = ou === null ? (dossiers.data?.dossiers ?? []) : (feuille.data?.enfants ?? []);

  const chemin = ou === null ? [] : (feuille.data?.chemin ?? []);
  const ici = chemin[chemin.length - 1] ?? null;
  const racine = chemin[0] ?? null;
  const couleur = ou === null ? null : (feuille.data?.couleur ?? null);
  const interdits = new Set([bulleId]);

  return (
    <div
      className="deplacer"
      /* La teinte du dossier d'arrivée descend jusqu'au bouton de dépôt et à sa lueur. */
      style={{ ['--teinte' as string]: teinteOuAccent(couleur) }}
    >
      <div className="deplacer-tete">
        <p className="deplacer-consigne">
          Où l’envoyer&nbsp;? Clique pour descendre, dépose au niveau où tu es.
        </p>
        <div className="deplacer-reprises">
          <button type="button" onClick={() => setOu(null)}>
            Recommencer
          </button>
          <button type="button" onClick={onRenoncer}>
            Renoncer
          </button>
        </div>
      </div>

      <nav className="deplacer-fil" aria-label="où tu descends">
        <button type="button" className="deplacer-racine" onClick={() => setOu(null)}>
          {m.mesDossiers}
        </button>

        {chemin.map((maillon, i) => {
          const estIci = i === chemin.length - 1;
          return (
            <span key={maillon.id} className="deplacer-maillon">
              <span className="deplacer-chevron" aria-hidden="true">
                ›
              </span>
              <button
                type="button"
                className={estIci ? 'deplacer-ici' : ''}
                aria-current={estIci ? 'true' : undefined}
                onClick={() => setOu(maillon.id)}
              >
                <span className="deplacer-point" aria-hidden="true" />
                {maillon.titre}
              </button>
            </span>
          );
        })}

        {ici && <span className="deplacer-tu-es-ici">Tu es ici</span>}
      </nav>

      {/* Ce qu'il y a dedans se montre EN LIGNE : c'est un lieu qu'on parcourt, pas une liste
          qu'on trie. Le chevron dit qu'on peut encore descendre. */}
      <div className="deplacer-dedans">
        <span className="deplacer-dedans-mot">Dedans&nbsp;:</span>

        {dedans.length === 0 && (
          <span className="deplacer-vide">
            {ou === null ? m.aucun : 'Rien — dépose ici'}
          </span>
        )}

        {dedans.map((enfant) => (
          <button
            key={enfant.id}
            type="button"
            className="deplacer-chose"
            disabled={interdits.has(enfant.id)}
            onClick={() => setOu(enfant.id)}
            title={
              interdits.has(enfant.id)
                ? 'C’est la bulle que tu déplaces'
                : `Descendre dans ${enfant.titre}`
            }
          >
            {enfant.titre}
            {enfant.nbEnfants > 0 && (
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M4 2.2L7.8 6L4 9.8" stroke="var(--encre-mate)" strokeWidth="1.5" />
              </svg>
            )}
          </button>
        ))}
      </div>

      <div className="deplacer-pied">
        {/* Tant que le niveau choisi n'est pas lu, le bouton dirait « Sortir … comme dossier » :
            un clic à ce moment-là rangerait la bulle à la racine. Il attend. */}
        <button
          className="deplacer-deposer"
          type="button"
          disabled={ou !== null && !feuille.data}
          onClick={() => onDeposer(ou, racine?.id ?? null)}
        >
          {ici ? `Déposer dans ${ici.titre}` : m.sortirComme(titre)}
        </button>
        <p className="deplacer-suite">{ceQuiSuit(combienDedans, racine?.titre ?? null, m.deviendraUn)}</p>
      </div>
    </div>
  );
}

/**
 * Ce que le dépôt entraîne, dit AVANT. Une bulle ne part jamais seule : son contenu suit,
 * sa condition verticale se refait toute seule, et elle change de couleur en changeant de
 * dossier — trois effets qu'on découvrirait sinon après coup.
 */
function ceQuiSuit(combienDedans: number, racine: string | null, deviendraUn: string): string {
  const emporte =
    combienDedans > 0
      ? `Elle part avec ${motDeCompte(combienDedans, { lesDevantUnChiffre: true })} qu’elle contient`
      : 'Elle part seule';

  if (!racine) {
    return `${emporte}, et ${deviendraUn}`;
  }
  return `${emporte}, sa condition suit toute seule, et elle prendra la couleur de ${racine}.`;
}
